import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useJobStore from "../store/jobStore";
import { useAuthStore } from "../store/authStore";
import StyleSelector from "../components/StyleSelector";

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  // Get only necessary state/actions from the Zustand store
  const { error, resetJobState, setError } = useJobStore();

  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();

  // Reset job state when mounting this page to ensure a fresh start
  useEffect(() => {
    resetJobState();
  }, [resetJobState]);

  // Redirect to purchase page if user has zero credits on initial load
  useEffect(() => {
    // Ensure the user object is loaded before checking credits
    if (user) {
      const totalCredits = (user.free_credits ?? 0) + (user.paid_credits ?? 0);
      if (totalCredits === 0) {
        if (user.is_anonymous) {
          navigate("/login", {
            state: { messageKey: "sign_in_for_credits" },
          });
        } else {
          navigate("/purchase", {
            state: { messageKey: "insufficient_credits_redirect" },
          });
        }
        // Track insufficient credits redirect on initial load
        window.trackEvent?.("insufficient_credits_redirect", {
          trigger: "home_page_style_selection",
        });
      }
    }
    // Dependency array includes user, so this runs when user state changes (e.g., after login)
  }, [user, navigate]);

  // Handle errors passed via URL query parameters
  useEffect(() => {
    const urlErrorCode = searchParams.get("error_code");
    const urlErrorDescription = searchParams.get("error_description");

    if (urlErrorCode === "identity_already_exists") {
      // Use a specific translation key or the description from the URL
      const message =
        t(
          `errors.${urlErrorCode}`,
          urlErrorDescription || t("errors.unknown")
        ) ||
        urlErrorDescription ||
        t("errors.unknown");

      setError(message);

      // Optionally remove the error params from URL after handling
      // navigate(location.pathname, { replace: true }); // Consider UX implications
    }
    // Only run once on mount or when searchParams change (though they likely won't after initial load)
  }, [searchParams, setError, t, navigate]);

  // Directly return the JSX for style selection
  return (
    <div className="w-full max-w-4xl mx-auto py-12 px-4 text-center">
      {/* Display errors from URL parameters */}
      {error && (
        <div className="mb-6 p-4 bg-red-900 bg-opacity-50 text-red-300 rounded-lg border border-red-800">
          {t(error, error)}
        </div>
      )}

      <h2 className="text-4xl font-bold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-500">
        {t("homePage.styleSelector.title", "Choose Your Style")}
      </h2>
      <div className="w-full mb-8">
        <StyleSelector />
      </div>
    </div>
  );
};

export default HomePage;
